import { DEFAULT_LOCKED_WIDTH_CM } from '../constants/editorDefaults';
import { cmToMm, pxToMm } from './editorUnits';
import { computeSizeFromLockedWidth } from './itemSizing';
import { measureFullSvgDocumentContentBounds, measureFullSvgDocumentPerimeter, type SvgContentBounds } from './pathLength';
import { buildDisplaySvg, parseSvgViewBox, resolveImageSvgSource, type SvgTrimViewBox } from './svgDocument';
import { buildProcessedImageUploadPatch } from './imageColorUpload';
import type { ColorOption } from './itemMetrics';
import type { ImageItem } from '../Reducers/Configurator/types';

const TRIM_EPSILON = 0.5;

export type ImageTrimFields = {
  trimViewBoxX: number;
  trimViewBoxY: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
  baseWidth: number;
  baseHeight: number;
};

export type ImageUploadPreview = {
  width?: number;
  height?: number;
  imageData?: Record<string, unknown>;
};

/** Fallback layout when the upload URL is not SVG (server normally converts vectors to SVG). */
export function buildNonSvgImageUploadPatch(
  url: string,
  preview: ImageUploadPreview = {}
): Partial<ImageItem> {
  const fallbackW = preview.width || 100;
  const fallbackH = preview.height || 100;
  const baseWidth = pxToMm(fallbackW);
  const baseHeight = pxToMm(fallbackH);
  const defaultWidth = cmToMm(DEFAULT_LOCKED_WIDTH_CM);
  const { width, height, scale } = computeSizeFromLockedWidth(baseWidth, baseHeight, defaultWidth);

  return {
    url,
    svgSource: '',
    trimViewBoxX: 0,
    trimViewBoxY: 0,
    baseWidth,
    baseHeight,
    viewBoxWidth: fallbackW,
    viewBoxHeight: fallbackH,
    width,
    height,
    scale,
  };
}

export function imagePatchNeedsLayout(patch: Partial<ImageItem>): boolean {
  return (
    patch.url !== undefined ||
    patch.svgSource !== undefined ||
    patch.viewBoxWidth !== undefined ||
    patch.viewBoxHeight !== undefined ||
    patch.trimViewBoxX !== undefined ||
    patch.trimViewBoxY !== undefined
  );
}

function isMeaningfulTrim(
  bounds: SvgContentBounds,
  fullViewBox: SvgTrimViewBox
): boolean {
  if (!(bounds.width > 0) || !(bounds.height > 0)) return false;
  const fillsArtboard =
    bounds.x <= fullViewBox.x + TRIM_EPSILON &&
    bounds.y <= fullViewBox.y + TRIM_EPSILON &&
    bounds.x + bounds.width >= fullViewBox.x + fullViewBox.width - TRIM_EPSILON &&
    bounds.y + bounds.height >= fullViewBox.y + fullViewBox.height - TRIM_EPSILON;
  return !fillsArtboard;
}

function trimFieldsFromBounds(bounds: SvgContentBounds): ImageTrimFields {
  return {
    trimViewBoxX: bounds.x,
    trimViewBoxY: bounds.y,
    viewBoxWidth: bounds.width,
    viewBoxHeight: bounds.height,
    baseWidth: pxToMm(bounds.width),
    baseHeight: pxToMm(bounds.height),
  };
}

function noTrimFields(fullViewBox: SvgTrimViewBox): ImageTrimFields {
  return {
    trimViewBoxX: fullViewBox.x,
    trimViewBoxY: fullViewBox.y,
    viewBoxWidth: fullViewBox.width,
    viewBoxHeight: fullViewBox.height,
    baseWidth: pxToMm(fullViewBox.width),
    baseHeight: pxToMm(fullViewBox.height),
  };
}

function measureContentBounds(
  svgSource: string,
  fallbackWidth: number,
  fallbackHeight: number
): SvgContentBounds | null {
  return measureFullSvgDocumentContentBounds(svgSource, fallbackWidth, fallbackHeight);
}

export function buildImageTrimFields(input: {
  svgSource: string;
  fallbackWidth: number;
  fallbackHeight: number;
}): ImageTrimFields {
  const fullViewBox = parseSvgViewBox(input.svgSource, input.fallbackWidth, input.fallbackHeight);
  const bounds = measureContentBounds(
    input.svgSource,
    fullViewBox.width,
    fullViewBox.height
  );

  if (!bounds || !isMeaningfulTrim(bounds, fullViewBox)) {
    return noTrimFields(fullViewBox);
  }

  return trimFieldsFromBounds(bounds);
}

export function applyImageTrimToItem(item: ImageItem, trim: ImageTrimFields): Partial<ImageItem> {
  const lockedWidth = item.width > 0 ? item.width : trim.baseWidth;
  const { width, height, scale } = computeSizeFromLockedWidth(trim.baseWidth, trim.baseHeight, lockedWidth);

  return {
    trimViewBoxX: trim.trimViewBoxX,
    trimViewBoxY: trim.trimViewBoxY,
    viewBoxWidth: trim.viewBoxWidth,
    viewBoxHeight: trim.viewBoxHeight,
    baseWidth: trim.baseWidth,
    baseHeight: trim.baseHeight,
    width,
    height,
    scale,
  };
}

export async function resolveImageItemLayout(
  item: ImageItem,
  colorOptions: ColorOption[] = []
): Promise<Partial<ImageItem>> {
  const svgSource = await resolveImageSvgSource(item);
  if (!svgSource) {
    return { sourcePerimeter: 0 };
  }

  const fullViewBox = parseSvgViewBox(svgSource, item.viewBoxWidth, item.viewBoxHeight);

  const preliminaryTrim = buildImageTrimFields({
    svgSource,
    fallbackWidth: fullViewBox.width,
    fallbackHeight: fullViewBox.height,
  });

  const preliminarySize = applyImageTrimToItem(item, preliminaryTrim);

  const preliminaryTrimViewBox: SvgTrimViewBox = {
    x: preliminaryTrim.trimViewBoxX,
    y: preliminaryTrim.trimViewBoxY,
    width: preliminaryTrim.viewBoxWidth,
    height: preliminaryTrim.viewBoxHeight,
  };
  const displaySvg = buildDisplaySvg(svgSource, preliminaryTrimViewBox);

  const merged: ImageItem = {
    ...item,
    svgSource,
    ...preliminarySize,
  };

  const processedPatch = buildProcessedImageUploadPatch(displaySvg, merged, colorOptions, item.imageData);
  const processedSvgSource = processedPatch?.svgSource || displaySvg;

  const finalTrim = buildImageTrimFields({
    svgSource: processedSvgSource,
    fallbackWidth: preliminaryTrim.viewBoxWidth,
    fallbackHeight: preliminaryTrim.viewBoxHeight,
  });

  const trimChanged =
    finalTrim.trimViewBoxX !== preliminaryTrim.trimViewBoxX ||
    finalTrim.trimViewBoxY !== preliminaryTrim.trimViewBoxY ||
    finalTrim.viewBoxWidth !== preliminaryTrim.viewBoxWidth ||
    finalTrim.viewBoxHeight !== preliminaryTrim.viewBoxHeight;

  const outputSvgSource = trimChanged
    ? buildDisplaySvg(processedSvgSource, {
        x: finalTrim.trimViewBoxX,
        y: finalTrim.trimViewBoxY,
        width: finalTrim.viewBoxWidth,
        height: finalTrim.viewBoxHeight,
      })
    : processedSvgSource;

  const sizeFields = applyImageTrimToItem(item, finalTrim);

  const sourcePerimeter = await measureFullSvgDocumentPerimeter(
    outputSvgSource,
    finalTrim.viewBoxWidth,
    finalTrim.viewBoxHeight
  );

  return {
    svgSource: outputSvgSource,
    colorAreas: processedPatch?.colorAreas || [],
    initialColorAreas: processedPatch?.colorAreas || [],
    colorAreaBounds: processedPatch?.colorAreaBounds || [],
    ...(processedPatch?.imageData ? { imageData: processedPatch.imageData } : {}),
    ...sizeFields,
    sourcePerimeter,
  };
}
